import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence } from "motion/react";
import { useForm } from "react-hook-form";
import { generatePath, useNavigate } from "react-router-dom";
import { t } from "ttag";
import { z } from "zod";

import { ErrorModal } from "@/app/components/legacy-modals";
import { ROUTES } from "@/app/router";
import type { ModalProps } from "@/shared/models/legacy-types";
import {
  Alert,
  Button,
  CheckableCardsSkeleton,
  Collapse,
  Dialog,
  Form,
  Pills3Icon,
} from "@/shared/ui";
import { useAvailableRefillRequests } from "../hooks";
import { MY_MEDS_ROUTE } from "../router";
import { RefillOptionsRadioGroup } from "./refill-options-radio-group";

const formSchema = z.object({
  requestProductSlug: z.string().min(1, {
    error: () => t`Please select a refillable product to proceed`,
  }),
});

export const SelectRefillModal = ({ show, onClose }: ModalProps) => {
  const {
    data: availableRefillRequests,
    isSuccess,
    isLoading,
    isError,
  } = useAvailableRefillRequests();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { requestProductSlug: "" },
  });

  const navigate = useNavigate();

  const onSubmit = form.handleSubmit(({ requestProductSlug }) => {
    const selectedRequest = availableRefillRequests?.find(
      (request) => request.product.slug === requestProductSlug,
    );

    if (selectedRequest) {
      if (selectedRequest.hasPendingActions) {
        return navigate(ROUTES.HOME);
      }

      if (selectedRequest.isSyncRequired) {
        navigate(ROUTES.CONSULTATIONS.PURPOSE);
      } else {
        navigate(generatePath(MY_MEDS_ROUTE.REFILL_REQUEST), {
          state: selectedRequest.product,
        });
      }
    }
  });

  const canConfirmAndContinue = form.formState.isDirty;

  const handleClose = () => {
    onClose();
    form.reset();
  };

  if (isError) {
    return <ErrorModal open={show} onOpenChange={onClose} />;
  }

  return (
    <Form.Provider {...form}>
      <Dialog.Root open={show} onOpenChange={handleClose}>
        <Dialog.Content>
          <Dialog.Header icon={Pills3Icon}>
            <Dialog.Title>{t`New medication request`}</Dialog.Title>

            <Dialog.Description>
              {t`You can only pick one med at a time. Once you're done, you can come back and grab another if you need to!`}
            </Dialog.Description>
          </Dialog.Header>

          {isLoading && <CheckableCardsSkeleton />}

          <form id="select-refill-form" onSubmit={onSubmit}>
            {isSuccess && (
              <Form.Field
                control={form.control}
                name="requestProductSlug"
                render={({ field: { onChange, ...field } }) => {
                  const refillWithPendingActions =
                    availableRefillRequests?.find(
                      (request) =>
                        request.product.slug ===
                        form.getValues("requestProductSlug"),
                    )?.hasPendingActions;

                  return (
                    <Form.Item className="gap-2">
                      <Form.Control>
                        <RefillOptionsRadioGroup
                          onValueChange={onChange}
                          refillRequests={availableRefillRequests}
                          {...field}
                        />
                      </Form.Control>

                      <AnimatePresence>
                        {refillWithPendingActions && (
                          <Collapse key="pending-actions-alert">
                            <Alert type="warning">
                              {t`You'll need updated labs before we can ship your meds. You can schedule them anytime from Home.`}
                            </Alert>
                          </Collapse>
                        )}
                      </AnimatePresence>
                    </Form.Item>
                  );
                }}
              />
            )}
          </form>

          <Dialog.Footer>
            <Button size="lg" variant="secondary" onClick={handleClose}>
              {t`Cancel request`}
            </Button>

            <Button
              form="select-refill-form"
              type="submit"
              size="lg"
              disabled={!canConfirmAndContinue}
            >
              {t`Confirm and continue`}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </Form.Provider>
  );
};
